import React from 'react';

const pulseKeyframes = `
@keyframes skeleton-pulse {
  0%, 100% { opacity: 0.4; }
  50% { opacity: 1; }
}
`;

// Inject keyframes once
if (typeof document !== 'undefined' && !document.getElementById('skeleton-styles')) {
  const style = document.createElement('style');
  style.id = 'skeleton-styles';
  style.textContent = pulseKeyframes;
  document.head.appendChild(style);
}

const SkeletonBlock = ({ width = '100%', height = '16px', borderRadius = '8px', style = {} }) => (
  <div
    style={{
      width,
      height,
      borderRadius,
      background: '#E5E7EB',
      animation: 'skeleton-pulse 1.5s ease-in-out infinite',
      ...style,
    }}
  />
);

export const DashboardSkeleton = () => (
  <div style={{ minHeight: '100vh', background: '#F9FAFB', padding: '32px 24px' }}>
    <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
        <div>
          <SkeletonBlock width="240px" height="36px" />
          <SkeletonBlock width="180px" height="16px" style={{ marginTop: '8px' }} />
        </div>
        <SkeletonBlock width="120px" height="40px" borderRadius="12px" />
      </div>

      {/* Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px', marginBottom: '32px' }}>
        {[1, 2, 3].map((i) => (
          <div key={i} style={{ background: '#fff', borderRadius: '16px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
            <SkeletonBlock width="40px" height="40px" borderRadius="12px" />
            <SkeletonBlock width="120px" height="14px" style={{ marginTop: '16px' }} />
            <SkeletonBlock width="80px" height="28px" style={{ marginTop: '8px' }} />
            <SkeletonBlock width="100%" height="6px" style={{ marginTop: '16px' }} />
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div style={{ background: '#fff', borderRadius: '16px', padding: '32px', marginBottom: '32px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
        <SkeletonBlock width="160px" height="20px" style={{ marginBottom: '20px' }} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
          <SkeletonBlock width="100%" height="48px" borderRadius="12px" />
          <SkeletonBlock width="100%" height="48px" borderRadius="12px" />
        </div>
      </div>

      {/* Recent Events */}
      <div style={{ background: '#fff', borderRadius: '16px', padding: '32px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '24px' }}>
          <SkeletonBlock width="200px" height="24px" />
          <SkeletonBlock width="140px" height="40px" borderRadius="12px" />
        </div>
        {[1, 2, 3].map((i) => (
          <div key={i} style={{ padding: '20px', border: '2px solid #F3F4F6', borderRadius: '16px', marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <SkeletonBlock width="32px" height="32px" borderRadius="8px" />
                <SkeletonBlock width="180px" height="18px" />
              </div>
              <SkeletonBlock width="80px" height="28px" borderRadius="14px" />
            </div>
            <div style={{ display: 'flex', gap: '24px', marginBottom: '12px' }}>
              <SkeletonBlock width="140px" height="14px" />
              <SkeletonBlock width="100px" height="14px" />
            </div>
            <SkeletonBlock width="100%" height="6px" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

export const EventListSkeleton = () => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
    {[1, 2, 3, 4].map((i) => (
      <div key={i} style={{ padding: '20px', border: '2px solid #F3F4F6', borderRadius: '16px', background: '#fff' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <SkeletonBlock width="28px" height="28px" borderRadius="8px" />
            <SkeletonBlock width="200px" height="18px" />
          </div>
          <SkeletonBlock width="80px" height="28px" borderRadius="14px" />
        </div>
        <div style={{ display: 'flex', gap: '24px' }}>
          <SkeletonBlock width="140px" height="14px" />
          <SkeletonBlock width="120px" height="14px" />
        </div>
      </div>
    ))}
  </div>
);

export const ParticipantSkeleton = () => (
  <div style={{
    minHeight: '100vh',
    background: '#6D28D9',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
  }}>
    <div style={{
      background: '#fff',
      borderRadius: '24px',
      padding: '40px',
      width: '100%',
      maxWidth: '500px',
      boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
    }}>
      {/* Event title area */}
      <div style={{ textAlign: 'center', marginBottom: '32px' }}>
        <SkeletonBlock width="64px" height="64px" borderRadius="16px" style={{ margin: '0 auto 16px' }} />
        <SkeletonBlock width="200px" height="24px" style={{ margin: '0 auto 8px' }} />
        <SkeletonBlock width="280px" height="14px" style={{ margin: '0 auto' }} />
      </div>

      {/* Form fields */}
      <div style={{ marginBottom: '20px' }}>
        <SkeletonBlock width="80px" height="14px" style={{ marginBottom: '8px' }} />
        <SkeletonBlock width="100%" height="44px" borderRadius="12px" />
      </div>
      <div style={{ marginBottom: '24px' }}>
        <SkeletonBlock width="60px" height="14px" style={{ marginBottom: '8px' }} />
        <SkeletonBlock width="100%" height="44px" borderRadius="12px" />
      </div>

      {/* Date options */}
      {[1, 2, 3].map((i) => (
        <div key={i} style={{ padding: '16px', border: '2px solid #F3F4F6', borderRadius: '12px', marginBottom: '12px' }}>
          <SkeletonBlock width="140px" height="16px" style={{ marginBottom: '10px' }} />
          <div style={{ display: 'flex', gap: '8px' }}>
            <SkeletonBlock width="80px" height="36px" borderRadius="8px" />
            <SkeletonBlock width="80px" height="36px" borderRadius="8px" />
            <SkeletonBlock width="80px" height="36px" borderRadius="8px" />
          </div>
        </div>
      ))}

      {/* Submit button */}
      <SkeletonBlock width="100%" height="48px" borderRadius="12px" style={{ marginTop: '24px' }} />
    </div>
  </div>
);

export default SkeletonBlock;
